import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiClient } from '../lib/api-client';

type Plan = {
  id: string;
  tier: string;
  name: string;
  displayName: Record<string, string>;
  description: Record<string, string>;
  prices: Array<{
    id: string;
    currencyCode: string;
    priceMonthly: number | null;
    priceYearly: number | null;
  }>;
  limits: Record<string, number | boolean | null>;
  features: Record<string, string[]>;
  displayOrder: number;
  isActive: boolean;
  isVisible: boolean;
};

function IssueTicketPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    planId: '',
    validDays: 30,
    planExpiresAt: '',
    maxRedemptions: 1,
    redemptionDeadline: '',
    memo: '',
    overrideLimits: false,
    limitsOverride: {
      monthlyPostsLimit: null as number | null,
      pendingPostsLimit: null as number | null,
      scheduleIntervalMinutes: null as number | null,
      maxPostLength: null as number | null,
      maxScheduleDays: null as number | null,
      markdownSupport: null as boolean | null,
      maxImagesPerPost: null as number | null,
      maxImageSizeMb: null as number | null,
      threadPosts: null as boolean | null,
      threadPostsLimit: null as number | null,
      videoUpload: null as boolean | null,
      maxVideoSizeMb: null as number | null
    }
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      // Include invisible plans for ticket issuance (admin can issue tickets for hidden plans)
      const data = await apiClient.getPlans({ includeInvisible: true });
      // Only filter out inactive plans (visible/invisible doesn't matter for tickets)
      setPlans(data.plans.filter(p => p.isActive));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Build limits override object if enabled
      const limitsOverride = formData.overrideLimits
        ? Object.fromEntries(
            Object.entries(formData.limitsOverride).filter(
              ([, value]) => value !== null && value !== undefined
            )
          )
        : undefined;

      // Convert datetime-local values to ISO 8601 with timezone
      const convertToISOWithTimezone = (datetimeLocal: string | undefined): string | undefined => {
        if (!datetimeLocal || datetimeLocal.trim() === '') return undefined;
        // datetime-local の値はローカル時刻なので、Date オブジェクトに変換して ISO 文字列を生成
        // これによりブラウザのタイムゾーンが自動的に適用される
        const date = new Date(datetimeLocal);
        return isNaN(date.getTime()) ? undefined : date.toISOString();
      };

      const result = await apiClient.issueTicket({
        planId: formData.planId,
        validDays: formData.validDays,
        planExpiresAt: convertToISOWithTimezone(formData.planExpiresAt),
        maxRedemptions: formData.maxRedemptions,
        redemptionDeadline: convertToISOWithTimezone(formData.redemptionDeadline),
        memo: formData.memo || undefined,
        limitsOverride:
          limitsOverride && Object.keys(limitsOverride).length > 0
            ? (limitsOverride as Record<string, number | boolean | null>)
            : undefined
      });

      setSuccess(`Ticket issued successfully: ${result.ticket.code}`);

      // Redirect to ticket detail page after 2 seconds
      setTimeout(() => {
        navigate(`/tickets/${result.ticket.id}`);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to issue ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'validDays' || name === 'maxRedemptions' ? parseInt(value) || 0 : value
    }));
  };

  if (plansLoading) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='text-lg text-gray-600 dark:text-gray-400'>Loading plans...</div>
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-2xl space-y-6'>
      <div className='flex items-center gap-4'>
        <button
          onClick={() => navigate('/tickets')}
          className='text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
        >
          ← Back to Tickets
        </button>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Issue New Ticket</h1>
      </div>

      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <p className='text-red-800 dark:text-red-200'>{error}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='space-y-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800 dark:shadow-gray-900/50'
      >
        {/* Plan Selection */}
        <div>
          <label
            htmlFor='planId'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Plan <span className='text-red-500 dark:text-red-400'>*</span>
          </label>
          <select
            id='planId'
            name='planId'
            value={formData.planId}
            onChange={handleChange}
            required
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
          >
            <option value=''>Select a plan</option>
            {plans
              .filter(p => p.tier !== 'FREE')
              .map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.displayName?.en || plan.name} ({plan.tier})
                </option>
              ))}
          </select>
          {formData.planId && (
            <div className='mt-2 rounded-md border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20'>
              {plans
                .filter(p => p.id === formData.planId)
                .map(plan => (
                  <div key={plan.id} className='space-y-3'>
                    <div>
                      <p className='font-semibold text-gray-900 dark:text-white'>
                        {plan.displayName?.en || plan.name}
                      </p>
                      <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                        {plan.description?.en}
                      </p>
                      <div className='mt-2 flex gap-4 text-sm text-gray-700 dark:text-gray-300'>
                        <span>
                          Monthly: $
                          {plan.prices?.find(p => p.currencyCode === 'usd')?.priceMonthly || 0}
                        </span>
                        <span>
                          Yearly: $
                          {plan.prices?.find(p => p.currencyCode === 'usd')?.priceYearly || 0}
                        </span>
                      </div>
                    </div>

                    <div className='border-t border-blue-200 pt-3 dark:border-blue-700'>
                      <p className='mb-2 text-xs font-semibold text-gray-600 uppercase dark:text-gray-400'>
                        Plan Limits Preview
                      </p>
                      <div className='grid grid-cols-1 gap-x-4 gap-y-2 text-xs text-gray-700 md:grid-cols-2 dark:text-gray-300'>
                        <div>
                          <span className='font-medium'>Monthly Posts:</span>{' '}
                          {plan.limits.monthlyPostsLimit === -1
                            ? 'Unlimited'
                            : plan.limits.monthlyPostsLimit}
                        </div>
                        <div>
                          <span className='font-medium'>Pending Posts:</span>{' '}
                          {plan.limits.pendingPostsLimit === -1
                            ? 'Unlimited'
                            : plan.limits.pendingPostsLimit}
                        </div>
                        <div>
                          <span className='font-medium'>Schedule Days:</span>{' '}
                          {plan.limits.maxScheduleDays === -1
                            ? 'Unlimited'
                            : `${plan.limits.maxScheduleDays} days`}
                        </div>
                        <div>
                          <span className='font-medium'>Schedule Interval:</span>{' '}
                          {plan.limits.scheduleIntervalMinutes} min
                        </div>
                        <div>
                          <span className='font-medium'>Max Post Length:</span>{' '}
                          {plan.limits.maxPostLength} chars
                        </div>
                        <div>
                          <span className='font-medium'>Markdown Support:</span>{' '}
                          {plan.limits.markdownSupport ? (
                            <span className='text-green-600 dark:text-green-400'>✓ Enabled</span>
                          ) : (
                            <span className='text-gray-400 dark:text-gray-500'>Disabled</span>
                          )}
                        </div>
                        <div>
                          <span className='font-medium'>Max Images/Post:</span>{' '}
                          {plan.limits.maxImagesPerPost}
                        </div>
                        <div>
                          <span className='font-medium'>Max Image Size:</span>{' '}
                          {plan.limits.maxImageSizeMb} MB
                        </div>
                        <div>
                          <span className='font-medium'>Thread Posts:</span>{' '}
                          {plan.limits.threadPosts
                            ? `Enabled (${plan.limits.threadPostsLimit})`
                            : 'Disabled'}
                        </div>
                        <div>
                          <span className='font-medium'>Video Upload:</span>{' '}
                          {plan.limits.videoUpload ? (
                            <span className='text-green-600 dark:text-green-400'>✓ Enabled</span>
                          ) : (
                            <span className='text-gray-400 dark:text-gray-500'>Disabled</span>
                          )}
                        </div>
                        {plan.limits.videoUpload && (
                          <>
                            <div>
                              <span className='font-medium'>Max Video Size:</span>{' '}
                              {plan.limits.maxVideoSizeMb === -1
                                ? 'Unlimited'
                                : `${plan.limits.maxVideoSizeMb} MB`}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Valid Days */}
        <div>
          <label
            htmlFor='validDays'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Valid Days <span className='text-red-500 dark:text-red-400'>*</span>
          </label>
          <input
            type='number'
            id='validDays'
            name='validDays'
            value={formData.validDays}
            onChange={handleChange}
            min='1'
            max='3650'
            required
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Number of days the plan will be valid after redemption (1-3650 days). Ignored if Plan
            Absolute Expiration Date is set.
          </p>
        </div>

        {/* Plan Absolute Expiration Date */}
        <div>
          <label
            htmlFor='planExpiresAt'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Plan Absolute Expiration Date (Optional)
          </label>
          <input
            type='datetime-local'
            id='planExpiresAt'
            name='planExpiresAt'
            value={formData.planExpiresAt}
            onChange={handleChange}
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            If set, all users&apos; plans will expire on this date regardless of when they redeem
            the ticket. This overrides the Valid Days setting.
          </p>
        </div>

        {/* Max Redemptions */}
        <div>
          <label
            htmlFor='maxRedemptions'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Max Redemptions <span className='text-red-500 dark:text-red-400'>*</span>
          </label>
          <input
            type='number'
            id='maxRedemptions'
            name='maxRedemptions'
            value={formData.maxRedemptions}
            onChange={handleChange}
            min='1'
            max='10000'
            required
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Maximum number of times this ticket can be redeemed (1 = single-use, higher numbers for
            campaigns)
          </p>
        </div>

        {/* Redemption Deadline */}
        <div>
          <label
            htmlFor='redemptionDeadline'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Redemption Deadline (Optional)
          </label>
          <input
            type='datetime-local'
            id='redemptionDeadline'
            name='redemptionDeadline'
            value={formData.redemptionDeadline}
            onChange={handleChange}
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            If set, the ticket must be redeemed before this date
          </p>
        </div>

        {/* Memo */}
        <div>
          <label
            htmlFor='memo'
            className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
          >
            Memo (Optional)
          </label>
          <textarea
            id='memo'
            name='memo'
            value={formData.memo}
            onChange={handleChange}
            rows={3}
            className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
            placeholder='Internal notes about this ticket...'
          />
        </div>

        {/* Limits Override */}
        <div className='border-t pt-4 dark:border-gray-700'>
          <div className='mb-3 flex items-center gap-3'>
            <input
              type='checkbox'
              id='overrideLimits'
              checked={formData.overrideLimits}
              onChange={e => setFormData(prev => ({ ...prev, overrideLimits: e.target.checked }))}
              className='rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700'
            />
            <label
              htmlFor='overrideLimits'
              className='text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Override Plan Limits
            </label>
          </div>

          {formData.overrideLimits && (
            <div className='ml-6 space-y-3 rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20'>
              <p className='mb-3 text-xs text-yellow-800 dark:text-yellow-200'>
                Leave fields empty to use plan default values. Only override specific limits as
                needed.
              </p>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Monthly Posts Limit
                </label>
                <input
                  type='number'
                  value={formData.limitsOverride.monthlyPostsLimit ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        monthlyPostsLimit: e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  min='-1'
                  max='10000'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
                  placeholder='Leave empty for plan default'
                />
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(
                        p =>
                          `Plan default: ${p.limits.monthlyPostsLimit === -1 ? 'Unlimited' : p.limits.monthlyPostsLimit}`
                      )}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Pending Posts Limit
                </label>
                <input
                  type='number'
                  value={formData.limitsOverride.pendingPostsLimit ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        pendingPostsLimit: e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  min='-1'
                  max='1000'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
                  placeholder='Leave empty for plan default'
                />
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(p => `Plan default: ${p.limits.pendingPostsLimit || 'N/A'}`)}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Schedule Interval (minutes)
                </label>
                <select
                  value={formData.limitsOverride.scheduleIntervalMinutes ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        scheduleIntervalMinutes:
                          e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
                >
                  <option value=''>Use plan default</option>
                  <option value={1}>1 minute</option>
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                </select>
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(p => `Plan default: ${p.limits.scheduleIntervalMinutes || 'N/A'} min`)}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Max Post Length (characters)
                </label>
                <input
                  type='number'
                  value={formData.limitsOverride.maxPostLength ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        maxPostLength: e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  min='1'
                  max='300'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
                  placeholder='Leave empty for plan default'
                />
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(p => `Plan default: ${p.limits.maxPostLength || 'N/A'} chars`)}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Max Schedule Days
                </label>
                <input
                  type='number'
                  value={formData.limitsOverride.maxScheduleDays ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        maxScheduleDays: e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  min='-1'
                  max='365'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
                  placeholder='Leave empty for plan default'
                />
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(
                        p =>
                          `Plan default: ${p.limits.maxScheduleDays ?? 'N/A'} days (-1 = unlimited)`
                      )}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Thread Posts Enabled
                </label>
                <select
                  value={
                    formData.limitsOverride.threadPosts === null
                      ? ''
                      : formData.limitsOverride.threadPosts
                        ? 'true'
                        : 'false'
                  }
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        threadPosts: e.target.value === '' ? null : e.target.value === 'true'
                      }
                    }))
                  }
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
                >
                  <option value=''>Use plan default</option>
                  <option value='true'>Enabled</option>
                  <option value='false'>Disabled</option>
                </select>
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(p => `Plan default: ${p.limits.threadPosts ? 'Enabled' : 'Disabled'}`)}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Thread Posts Limit
                </label>
                <input
                  type='number'
                  value={formData.limitsOverride.threadPostsLimit ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        threadPostsLimit: e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  min='1'
                  max='25'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
                  placeholder='Leave empty for plan default'
                />
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(p => `Plan default: ${p.limits.threadPostsLimit || 'N/A'} posts`)}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Markdown Support
                </label>
                <select
                  value={
                    formData.limitsOverride.markdownSupport === null
                      ? ''
                      : formData.limitsOverride.markdownSupport
                        ? 'true'
                        : 'false'
                  }
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        markdownSupport: e.target.value === '' ? null : e.target.value === 'true'
                      }
                    }))
                  }
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
                >
                  <option value=''>Use plan default</option>
                  <option value='true'>Enabled</option>
                  <option value='false'>Disabled</option>
                </select>
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(
                        p => `Plan default: ${p.limits.markdownSupport ? 'Enabled' : 'Disabled'}`
                      )}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Max Images Per Post
                </label>
                <input
                  type='number'
                  value={formData.limitsOverride.maxImagesPerPost ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        maxImagesPerPost: e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  min='0'
                  max='4'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
                  placeholder='Leave empty for plan default'
                />
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(p => `Plan default: ${p.limits.maxImagesPerPost || 'N/A'} images`)}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Max Image Size (MB)
                </label>
                <input
                  type='number'
                  value={formData.limitsOverride.maxImageSizeMb ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        maxImageSizeMb: e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  min='1'
                  max='10'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
                  placeholder='Leave empty for plan default'
                />
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(p => `Plan default: ${p.limits.maxImageSizeMb || 'N/A'} MB`)}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Video Upload Enabled
                </label>
                <select
                  value={
                    formData.limitsOverride.videoUpload === null
                      ? ''
                      : formData.limitsOverride.videoUpload
                        ? 'true'
                        : 'false'
                  }
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        videoUpload: e.target.value === '' ? null : e.target.value === 'true'
                      }
                    }))
                  }
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:ring-blue-400'
                >
                  <option value=''>Use plan default</option>
                  <option value='true'>Enabled</option>
                  <option value='false'>Disabled</option>
                </select>
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(p => `Plan default: ${p.limits.videoUpload ? 'Enabled' : 'Disabled'}`)}
                </p>
              </div>

              <div>
                <label className='mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'>
                  Max Video Size (MB)
                </label>
                <input
                  type='number'
                  value={formData.limitsOverride.maxVideoSizeMb ?? ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      limitsOverride: {
                        ...prev.limitsOverride,
                        maxVideoSizeMb: e.target.value === '' ? null : parseInt(e.target.value)
                      }
                    }))
                  }
                  min='-1'
                  max='1000'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-400'
                  placeholder='Leave empty for plan default'
                />
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  {formData.planId &&
                    plans
                      .filter(p => p.id === formData.planId)
                      .map(
                        p =>
                          `Plan default: ${p.limits.maxVideoSizeMb === -1 ? 'Unlimited' : `${p.limits.maxVideoSizeMb || 'N/A'} MB`}`
                      )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className='flex gap-4'>
          <button
            type='submit'
            disabled={loading || !formData.planId}
            className='flex-1 rounded-md bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:bg-blue-500 dark:hover:bg-blue-600 dark:disabled:bg-gray-600'
          >
            {loading ? 'Issuing...' : 'Issue Ticket'}
          </button>
          <button
            type='button'
            onClick={() => navigate('/tickets')}
            className='rounded-md bg-gray-200 px-6 py-3 text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          >
            Cancel
          </button>
        </div>

        {/* Success Message (shown below submit button) */}
        {success && (
          <div className='rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20'>
            <p className='text-green-800 dark:text-green-200'>{success}</p>
          </div>
        )}
      </form>
    </div>
  );
}

export default IssueTicketPage;
